import React, { useEffect, useState } from 'react';
import { Box, Divider, Typography, useMediaQuery, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ReactGA from 'react-ga4';

import { headerStyle, subHeaderStyle, pageWrapper, titleUnderline, btnStyle, btnIconStyle, btnWrapper, textFieldStyle, infoBoxStyle, titleDivider } from './styles';
import HDTextField from '../../../hd-ui-kit/HDTextField';
import HDDropDown from '../../../hd-ui-kit/HDDropDown';
import HDButton from '../../../hd-ui-kit/HDButton';
import HDCheckBox from '../../../hd-ui-kit/HDCheckBox/HDCheckBox';
import formData from '../../../utils/form-data';
import Api from '../../../Api';
import { validateFields, validationOptions } from '../../../ValidationSchema';
import { isDemographicApplicable, reactGAEvents, ResponsiveThemeValue } from '../../../commons/commonFunctions';
import { formatTimezoneID, getTimeZoneOptions } from '../../../commons/timezones';
import HDSpinner from '../../../hd-ui-kit/HDSpinner/HDSpinner';
import { COUNTRIES } from '../../../utils/form-data/countries';
import FlagIcon from '../../../commons/FlagIcon';
const phoneUtil = require('google-libphonenumber').PhoneNumberUtil.getInstance();

// Helper function to extract first and last name from LinkedIn oauth data (supports both old oauth2 and new OIDC)
const extractLinkedInName = (oauthExtraData: any) => {
  // Try old oauth2 structure first
  const oldFirstName = oauthExtraData?.first_name?.localized?.en_US;
  const oldLastName = oauthExtraData?.last_name?.localized?.en_US;
  
  if (oldFirstName && oldLastName) {
    return { firstName: oldFirstName, lastName: oldLastName };
  }
  
  // Fall back to OIDC structure
  const firstName = oauthExtraData?.given_name || '';
  const lastName = oauthExtraData?.family_name || '';
  
  return { firstName, lastName };
};

interface PayloadObjectTypes {
  first_name: any;
  last_name: any;
  mobile_number: string;
  mobile_country_code: string;
  text_opt_in: boolean;
  country: string;
  gender: string;
  ethnicity: string;
  lgbtq: string;
  diversity: string;
  linkedin_url: string;
  iana_timezone: string;
}

function PersonalDetails({ data, onSuccess, timeZoneValue, linkedInURLRef }: any) {
  const theme = useTheme();
  const themeMode = theme.palette.mode;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const oauthExtraData = JSON.parse(sessionStorage.getItem('oauth_extra_data'));
  const { firstName: oauthFirstName, lastName: oauthLastName } = extractLinkedInName(oauthExtraData);
  const [firstname, setFirstname] = useState<string>(oauthFirstName || '');
  const [lastname, setLastname] = useState<string>(oauthLastName || '');
  const [phone, setPhone] = useState<string>('');
  const [countryCode, setCountryCode] = useState<string>('us');
  const [textOpt, setTextOpt] = useState<boolean>(false);
  const [linkedInUrl, setLinkedInUrl] = useState<string>('');
  const [country, setCountry] = useState<string>('');
  const [gender, setGender] = useState<string>('');
  const [ethnicity, setEthnicity] = useState<string>('');
  const [lgbtq, setLgbtq] = useState<string>('');
  const [diversity, setDiversity] = useState<string>('');
  const [errors, setErrors] = useState<any>({});
  const [timezone, setTimeZone] = useState({ value: '', label: '' });
  const [timezoneOptions, setTimeZoneOptions] = useState(getTimeZoneOptions('Other'));
  const [btnDisable, setBtnDisable] = useState(false);

  // Helper function to add mobile country code prefix to phone number
  const addMobileCountryCodePrefix = (phoneNumber: string, countryCodeValue: string): string => {
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    const selectedCountry = COUNTRIES.find((c) => c.country_code === countryCodeValue);
    const mobileCountryCodePrefix = selectedCountry?.mobile_country_code || '';
    // Remove the '+' sign from the prefix
    const prefixWithoutPlus = mobileCountryCodePrefix.replace('+', '');
    
    return `${prefixWithoutPlus}${cleanPhone}`;
  };

  // Helper function to remove mobile country code prefix from phone number
  const removeMobileCountryCodePrefix = (phoneNumber: string, countryCodeValue: string): string => {
    if (!phoneNumber) return '';
    const selectedCountry = COUNTRIES.find((c) => c.country_code === countryCodeValue);
    const mobileCountryCodePrefix = selectedCountry?.mobile_country_code || '';
    // Remove the '+' sign from the prefix for comparison
    const prefixWithoutPlus = mobileCountryCodePrefix.replace('+', '');

    // If phone number starts with the prefix (without +), remove it
    if (prefixWithoutPlus && phoneNumber.startsWith(prefixWithoutPlus)) {
      return phoneNumber.slice(prefixWithoutPlus.length);
    }
    // Also check if it starts with the prefix including '+'
    if (mobileCountryCodePrefix && phoneNumber.startsWith(mobileCountryCodePrefix)) {
      return phoneNumber.slice(mobileCountryCodePrefix.length);
    }
    return phoneNumber;
  };

  useEffect(() => {
    setTimeZoneOptions(getTimeZoneOptions(country || 'Other'));
    if (country === 'United States of America' || country === 'United Kingdom' || country === 'Canada' || country === 'Australia') {
      return;
    } else if (country === '') {
      setLgbtq('');
      setDiversity('');
    } else {
      setLgbtq('');
    }
    return;
  }, [country]);

  const setFormDataFromContext = (data: { [key: string]: any }) => {
    if (!data) {
      setCountryCode('us');
      return;
    }
    setFirstname(data['first_name']);
    setLastname(data['last_name']);
    const phoneWithoutPrefix = removeMobileCountryCodePrefix(data['mobile_number'], data['mobile_country_code']);
    setPhone(phoneWithoutPrefix);
    setCountryCode(data['mobile_country_code']);
    setTextOpt(data['text_opt_in'] as unknown as boolean);
    setLinkedInUrl(data['linkedin_url']);
    setCountry(data['country']);
    setGender(data['gender'] === 'Not Applicable' ? '' : data['gender']);
    setEthnicity(data['ethnicity'] === 'Not Applicable' ? '' : data['ethnicity']);
    setLgbtq(data['lgbtq'] === 'Not Applicable' ? '' : data['lgbtq']);
    setDiversity(data['diversity']);
  };

  useEffect(() => {
    if (data) {
      setFormDataFromContext(data);
    } else {
      setCountryCode('us');
    }
    if (typeof timeZoneValue === 'string') {
      setTimeZone({ 'value': timeZoneValue, 'label': formatTimezoneID(timeZoneValue) });
    } else {
      setTimeZone(timeZoneValue);
    }
  }, [data, timeZoneValue]);

  const onSuccessSave = () => {
    ReactGA.event({
      category: 'Profile Edit - Personal Details',
      action: 'Save',
    });
    onSuccess(true);
    window.scrollTo(0, 0);
  };

  const validate = () => {
    let errorObject: any = {};

    // phone validation
    try {
      // Convert country code to uppercase for google-libphonenumber library
      const number = phoneUtil.parse(phone, countryCode.toUpperCase());
      
      // Handle Puerto Rico numbers (area codes 787, 939) when US is selected
      // Google-libphonenumber recognizes PR as separate region even though it uses +1
      let validationRegion = countryCode.toUpperCase();
      if (countryCode === 'us') {
        const areaCode = phone.substring(0, 3);
        if (areaCode === '787' || areaCode === '939') {
          validationRegion = 'PR';
        }
      }
      
      const numberIsValid = phoneUtil.isValidNumberForRegion(number, validationRegion);
      if (!numberIsValid) {
        let countryRegion;
        switch (countryCode) {
          case 'us':
            countryRegion = 'U.S.';
            break;
          case 'gb':
            countryRegion = 'UK';
            break;
          default:
            countryRegion = countryCode?.toUpperCase();
            break;
        }
        errorObject['mobile_number'] = `Mobile number is not valid for ${countryRegion} region`;
      }
    } catch (e) {
      errorObject['mobile_number'] = 'The string supplied did not seem to be a phone number';
    }
    return errorObject;
  };

  const onSave = () => {
    setBtnDisable(true);
    reactGAEvents('my-account', 'save');
    onSuccess(false);
    setErrors({});
    const chkForTagsRegValue = '^linkedin\\.com\\/in\\/[\\w\\u00C0-\\u017F./_-]{5,256}\\/?$';
    const chkForTagsRegEx = new RegExp(chkForTagsRegValue, 'i');
    const customPath = linkedInUrl.split('/in/')[1]?.replace('/', '');
    const containsLinkedIn = customPath && /linkedin/i.test(customPath);

    const firstNameMsg = validateFields.validate({ name: firstname }, validationOptions)?.error?.details[0]?.message;
    const lastNameMsg = validateFields.validate({ name: lastname }, validationOptions)?.error?.details[0]?.message;
    const countryCodeMsg = validateFields.validate({ dropDown: countryCode }, validationOptions)?.error?.details[0]?.message;
    const mobileNumberMsg = validate()?.mobile_number;
    const countryMsg = validateFields.validate({ dropDown: country }, validationOptions)?.error?.details[0]?.message;
    const timeZoneMsg = validateFields.validate({ dropDown: timezone?.value ? timezone?.value : '' }, validationOptions)?.error?.details[0]?.message;
    let genderMsg;
    if (isDemographicApplicable(country)) {
      genderMsg = validateFields.validate({ dropDown: gender }, validationOptions)?.error?.details[0]?.message;
    }

    if (firstNameMsg || lastNameMsg || countryCodeMsg || mobileNumberMsg || countryMsg || timeZoneMsg || genderMsg) {
      setErrors({
        first_name: firstNameMsg,
        last_name: lastNameMsg,
        country_code: countryCodeMsg,
        mobile_number: mobileNumberMsg,
        country: countryMsg,
        timezone: timeZoneMsg,
        gender: genderMsg,
      });
      setBtnDisable(false);
      return;
    }

    if (linkedInUrl != '' && (!chkForTagsRegEx.test(linkedInUrl) || containsLinkedIn)) {
      setErrors({ linkedin_url: 'Linked in URL should not contain HTML Tags/Special Characters' });
      setBtnDisable(false);
      return;
    }

    const defaultSelect = formData.declineSelectDefault;
    const payload: PayloadObjectTypes = {
      first_name: firstname,
      last_name: lastname,
      mobile_number: addMobileCountryCodePrefix(phone, countryCode),
      mobile_country_code: countryCode,
      text_opt_in: textOpt,
      country: country,
      gender:(!isDemographicApplicable(country)) ? 'Not Applicable' : gender || defaultSelect,
      ethnicity: (!isDemographicApplicable(country)) ? 'Not Applicable' : ethnicity || defaultSelect,
      lgbtq: (!isDemographicApplicable(country)) ? 'Not Applicable' : lgbtq || defaultSelect,
      diversity: diversity || defaultSelect,
      linkedin_url: linkedInUrl,
      iana_timezone: timezone?.value,
    };

    if (data) {
      Api.updatePersonalDetail(payload, data['id'] as never)
        .then((res) => {
          onApiSuccess(res);
        })
        .catch((err) => {
          console.log(err);
          setBtnDisable(false);
        });
    } else {
      // send data off to be saved
      Api.savePersonalDetail(payload)
        .then((res) => {
          onApiSuccess(res);
        })
        .catch((err) => {
          console.log(err);
          setBtnDisable(false);
        });
    }
  };

  const onApiSuccess = (res: any) => {
    if (res?.success) {
      // navigate to the next form
      setBtnDisable(false);
      onSuccessSave();
    } else {
      const { data } = res;
      setBtnDisable(false);
      setErrors(data);
    }
  };

  const sxStyleMarginBottom = { marginBottom: '20px' };

  return (
    <Box sx={pageWrapper(isMobile, themeMode)}>
      <Typography sx={headerStyle(themeMode)}>Personal Details</Typography>
      <Divider sx={titleUnderline(themeMode)} />

      <Box sx={infoBoxStyle(themeMode, 'top')}>
        <Typography sx={subHeaderStyle(themeMode)}>Contact Information</Typography>
        <Divider sx={titleDivider(themeMode)} />

        <HDTextField
          type={'text'}
          placeholder={'First name'}
          id={'firstName'}
          lblText={'First name'}
          sx={textFieldStyle}
          value={firstname}
          onClick={() => {
            reactGAEvents('my-account', 'first-name');
          }}
          onChange={({ target }: any) => {
            setFirstname(target.value);
          }}
          error={errors?.first_name}
          errorMsg={errors?.first_name ? errors.first_name : null}
        />

        <HDTextField
          type={'text'}
          placeholder={'Last name'}
          id={'lastName'}
          lblText={'Last name'}
          sx={textFieldStyle}
          value={lastname}
          onClick={() => {
            reactGAEvents('my-account', 'last-name');
          }}
          onChange={({ target }: any) => {
            setLastname(target.value);
          }}
          error={errors?.last_name}
          errorMsg={errors?.last_name ? errors.last_name : null}
        />
        <Box
          sx={{
            ...textFieldStyle,
            display: isMobile ? '' : 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            width:'100%',
            gap: '10px',
          }}
        >
          <Box sx={{ width: isMobile ? '100%' : countryCode ? '20%' : '17%' }}>
            <HDDropDown
              value={countryCode}
              dropDownItem={COUNTRIES.filter((country) => country.country_value !== 'Other').map((country) => ({
                keyName: (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FlagIcon countryCode={country.country_code} width={20} height={15} />
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        color: ResponsiveThemeValue(theme, '#002136', '#FFF'),
                      }}
                    >
                      {country.country_value}
                    </span>
                  </span>
                ) as any,
                keyValue: country.country_code,
              }))}
              fieldLabel={'Country Code '}
              error={errors?.country_code}
              onChange={({ target }: any) => {
                reactGAEvents('my-account', `country-code-${target.value}`);
                setCountryCode(target.value);
                setErrors((prevState: any) => ({ ...prevState, country_code: undefined }));
              }}
              id="telephone"
            />
          </Box>
          <Box sx={{ ...textFieldStyle, width: '100%' }}>
            <HDTextField
              prefixText={`${countryCode ? COUNTRIES.find((c) => c.country_code === countryCode)?.mobile_country_code : ''}`}
              type={'tel'}
              id={'mobileNumber'}
              lblText={'Mobile Number'}
              sx={{
                ...textFieldStyle,
                marginTop: isMobile ? '36px' : '0',
                '& .MuiFormLabel-root': {
                  marginBottom: isMobile ? '10px' : '9px',
                },
              }}
              value={phone}
              onClick={() => {
                reactGAEvents('my-account', 'mobile-number');
              }}
              onChange={({ target }: any) => {
                const numericValue = target.value.replace(/[^0-9]/g, '');
                setPhone(numericValue);
                setErrors((prevState: any) => ({ ...prevState, mobile_number: undefined }));
              }}
              error={errors?.mobile_number || validate()?.mobile_number}
              errorMsg={errors?.mobile_number ? errors.mobile_number : validate()?.mobile_number ? validate()?.mobile_number : null}
            />
          </Box>
        </Box>

        <Box sx={{ ...textFieldStyle, marginTop: '36px', marginBottom: '36px' }}>
          <HDCheckBox
            labelTxt={"It's ok to call or text me about Heidrick job openings."}
            checked={textOpt}
            value={textOpt}
            disabled={false}
            onChange={({ target }: any) => {
              reactGAEvents('my-account', 'ok-to-contact-hs-job-openings');
              setTextOpt(target.checked);
            }}
            error={errors?.text_opt_in}
            id="its-ok-to-call"
          />
        </Box>
        <Box ref={linkedInURLRef}>
          <HDTextField
            type={'text'}
            placeholder={'LinkedIn URL(optional)'}
            id={'linkedInURL'}
            lblText={'LinkedIn URL'}
            sx={textFieldStyle}
            value={linkedInUrl}
            onClick={() => {
              reactGAEvents('my-account', 'linkedin-url');
            }}
            onChange={({ target }: any) => {
              setLinkedInUrl(target.value);
            }}
            error={errors?.linkedin_url}
            errorMsg={errors?.linkedin_url ? 'Enter a valid LinkedIn URL. URL needs to be linkedin.com/in/….' : null}
          />
        </Box>

        <Box sx={sxStyleMarginBottom}>
          <HDDropDown
            value={country}
            dropDownItem={formData.countryFormList.map((ci) => ({ keyName: ci, keyValue: ci }))}
            fieldLabel={'Country of Residence'}
            error={errors?.country}
            errorMsg={errors?.country ? errors.country : null}
            onChange={({ target }: any) => {
              reactGAEvents('my-account', `country-residence-${target.value}`);
              setTimeZone({ value: '', label: '' });
              setGender('');
              setEthnicity('');
              setLgbtq('');
              setErrors((prevState: any) => ({ ...prevState, gender: undefined }));
              setCountry(target.value);
            }}
            id="country-of-residence"
          />
        </Box>

        <HDDropDown
          dataTestId={'Time zone'}
          fieldLabel="Time zone"
          disabled={false}
          defaultValue=""
          dropDownItem={[
            { keyValue: 1, keyName: 'Select 1' },
            { keyValue: 2, keyName: 'Select 2' },
            { keyValue: 3, keyName: 'Select 3' },
          ]}
          isAutoComplete={true}
          autoCompleteOptions={timezoneOptions}
          value={timezone}
          onChange={(value) => {
            reactGAEvents('my-account', `time-zone-${value?.label}`);
            setTimeZone(value);
          }}
          error={errors?.timezone}
          errorMsg={errors?.timezone ? errors.timezone : null}
          id="time-zone"
        />
      </Box>

      <Box sx={infoBoxStyle(themeMode)}>
        <Typography sx={subHeaderStyle(themeMode)}>Additional information</Typography>
        <Divider sx={titleDivider(themeMode)} />
        {!isDemographicApplicable(country) ? (
          <></>
        ) : (
          <>
            <Box sx={sxStyleMarginBottom}>
              <HDDropDown
                fieldLabel={'Gender'}
                showToolTip={false}
                toolTipText={'Coming Soon!'}
                value={gender}
                dropDownItem={formData.genderMenu(country).map((ci) => ({ keyName: ci, keyValue: ci }))}
                error={errors?.gender}
                errorMsg={errors?.gender ? errors.gender : null}
                onChange={({ target }: any) => {
                  reactGAEvents('my-account', `gender-${target.value}`);
                  setGender(target.value);
                }}
                id="gender"
                disabled={!formData.genderMenu(country).length}
              />
            </Box>
            <Box sx={sxStyleMarginBottom}>
              <HDDropDown
                fieldLabel={'Ethnicity (optional)'}
                showToolTip={false}
                toolTipText={'Coming Soon!'}
                value={ethnicity}
                dropDownItem={formData.ethnicityMenu(country).map((ci) => ({ keyName: ci, keyValue: ci }))}
                onChange={({ target }: any) => {
                  reactGAEvents('my-account', `ethnicity-${target.value}`);
                  setEthnicity(target.value);
                }}
                id="ethnicity"
                disabled={!formData.ethnicityMenu(country).length}
              />
            </Box>
            <Box sx={sxStyleMarginBottom}>
              <HDDropDown
                fieldLabel={'LGBTQ (optional)'}
                showToolTip={false}
                toolTipText={'Coming Soon!'}
                value={lgbtq}
                dropDownItem={formData.lgbtqMenu(country).map((ci) => ({ keyName: ci, keyValue: ci }))}
                error={errors?.lgbtq}
                errorMsg={errors?.lgbtq ? errors.lgbtq : null}
                onChange={({ target }: any) => {
                  reactGAEvents('my-account', `lgbtq-${target.value}`);
                  setLgbtq(target.value);
                }}
                disabled={!formData.lgbtqMenu(country).length}
                id="lgbtq"
              />
            </Box>
          </>
        )}
        <Box sx={sxStyleMarginBottom}>
          <HDDropDown
            fieldLabel={'Diversity, Non-Specific (optional)'}
            showToolTip={true}
            toolTipText={`Refers to individuals who identify as diverse but either do not fall into one of the above categories
           (could include but is not limited to veteran status or disability),
           or do not wish to share further information with the firm as to how they are diverse.`}
            value={diversity}
            dropDownItem={formData.diversityMenu(country).map((ci) => ({ keyName: ci, keyValue: ci }))}
            error={errors?.diversity}
            errorMsg={errors?.diversity ? errors.diversity : null}
            onChange={({ target }: any) => {
              reactGAEvents('my-account', `diversity-${target.value}`);
              setDiversity(target.value);
            }}
            id="diversity"
          />
        </Box>
      </Box>

      <Box sx={btnWrapper}>
        <HDButton text={'Save'} disabled={btnDisable} endIcon={btnDisable ? <HDSpinner size={'la-sm'} /> : <ArrowForwardIcon sx={btnIconStyle(themeMode)} />} variant={'contained'} sx={btnStyle(themeMode)} onClick={onSave} showAnimation={true} />
      </Box>
    </Box>
  );
}

export default PersonalDetails;
